import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  BookCheck,
  CheckCheck,
  Copy,
  Download,
  FileText,
  Link2Off,
  Loader2,
  MailCheck,
  PackageCheck,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { ReceivedPurchaseButton } from "@/components/purchases/ReceivedPurchaseButton";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  ClaimWindowCountdown,
  RadianStateChip,
} from "@/components/radian/RadianStateChip";
import {
  RadianEventTimeline,
  type RadianEventArtifactDownload,
  type RadianTimelineEvent,
} from "@/components/radian/RadianEventTimeline";
import {
  RADIAN_CLAIM_CONCEPTS,
  radianErrorMessage,
  saveBlob,
  shortUuid,
} from "@/components/radian/format";
import {
  useEmitReceivedDocumentEvent,
  useLinkReceivedPurchaseInvoice,
  useReceivedDocument,
  useReceivedDocumentArtifacts,
  useRecordPurchasePrefill,
  useRecordPurchaseFromReceived,
  useSyncReceivedDocumentEvents,
} from "@/lib/api/received-documents.queries";
import {
  getRadianEventArtifactBlob,
  getReceivedDocumentArtifactBlob,
  type RadianClaimConcept,
  type ReceivedDocumentArtifactName,
  type ReceiverEventCode,
} from "@/lib/api/received-documents";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute(
  "/companies/$companyId/recibidas/$receivedId",
)({
  component: ReceivedDocumentDetailPage,
});

const ACTION_ICON: Record<ReceiverEventCode, typeof MailCheck> = {
  "030": MailCheck,
  "031": ShieldAlert,
  "032": PackageCheck,
  "033": CheckCheck,
};

const DOC_ARTIFACT_EXT: Record<string, string> = {
  signedXML: "xml",
  attachedDocument: "xml",
  response: "json",
};

function ReceivedDocumentDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ReceivedDocumentDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function ReceivedDocumentDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, receivedId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
  }, [companyId, setActiveCompanyId]);

  const detailQuery = useReceivedDocument(companyId, receivedId);
  const emitEvent = useEmitReceivedDocumentEvent(companyId);
  const syncEvents = useSyncReceivedDocumentEvents(companyId);
  const linkMutation = useLinkReceivedPurchaseInvoice(companyId);

  const [pendingEventCode, setPendingEventCode] = useState<string | null>(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  if (detailQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <ErrorState
        title={t("recibidas.detail.error_title")}
        description={t("recibidas.detail.error_body")}
        error={detailQuery.error ?? undefined}
        onRetry={() => void detailQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const doc = detailQuery.data;

  async function onCopyCufe() {
    try {
      await navigator.clipboard.writeText(doc.cufe);
      toast.success(t("recibidas.toast.cufe_copied"));
    } catch {
      toast.error(t("recibidas.toast.copy_failed"));
    }
  }

  async function onEmitEvent(
    eventCode: ReceiverEventCode,
    claim?: { concept: RadianClaimConcept; description?: string },
  ): Promise<boolean> {
    setPendingEventCode(eventCode);
    try {
      const result = await emitEvent.mutateAsync({
        receivedId,
        input: { eventCode, ...(claim ? { claim } : {}) },
      });
      if (result.status === "accepted" && result.cude) {
        toast.success(
          t("recibidas.toast.event_done", {
            label: t(`recibidas.action.${eventCode}`),
            cude: shortUuid(result.cude),
          }),
        );
      } else if (result.status === "failed") {
        toast.error(
          result.dianStatusDescription ?? t("recibidas.toast.event_failed"),
        );
      } else {
        toast.info(t("recibidas.toast.event_pending"));
      }
      return true;
    } catch (err) {
      toast.error(radianErrorMessage(err, t));
      return false;
    } finally {
      setPendingEventCode(null);
    }
  }

  async function onSyncEvents() {
    try {
      const result = await syncEvents.mutateAsync({ receivedId });
      toast.success(
        t("recibidas.toast.sync_done", {
          reported: result.reported,
          upserted: result.upserted,
        }),
      );
    } catch (err) {
      toast.error(radianErrorMessage(err, t));
    }
  }

  async function onUnlink() {
    try {
      await linkMutation.mutateAsync({ receivedId, purchaseInvoiceId: null });
      toast.success(t("recibidas.toast.unlinked"));
    } catch (err) {
      toast.error(radianErrorMessage(err, t));
    }
  }

  async function onDownloadEventArtifact(
    event: RadianTimelineEvent,
    name: RadianEventArtifactDownload,
  ) {
    const key = `${event.id}:${name}`;
    setDownloadingKey(key);
    try {
      const blob = await getRadianEventArtifactBlob(
        companyId,
        receivedId,
        event.id,
        name,
      );
      const ext = name === "response" ? "json" : "xml";
      saveBlob(blob, `${event.documentId ?? event.id}-${name}.${ext}`);
    } catch (err) {
      toast.error(radianErrorMessage(err, t));
    } finally {
      setDownloadingKey(null);
    }
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/recibidas"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("recibidas.detail.back")}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-2xl font-semibold tracking-tight">
              {doc.documentId}
            </h1>
            <RadianStateChip state={doc.radianState} />
            <ClaimWindowCountdown
              state={doc.radianState}
              claimWindowEndsAt={doc.claimWindowEndsAt}
            />
            <Badge variant={doc.dianValidated ? "success" : "warning"}>
              {doc.dianValidated
                ? t("recibidas.dian.valid")
                : t("recibidas.dian.not_validated")}
            </Badge>
            <Badge variant="outline">
              {t(`recibidas.source.${doc.source}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {doc.supplier.name} · NIT {doc.supplier.nit}
            {doc.supplier.dv ? `-${doc.supplier.dv}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => void onSyncEvents()}
            disabled={syncEvents.isPending}
          >
            <RefreshCw
              className={
                syncEvents.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"
              }
            />
            {t("recibidas.detail.sync_events")}
          </Button>
          {!doc.purchaseInvoiceId ? (
            <ReceivedPurchaseButton
              companyId={companyId}
              receivedId={receivedId}
              onClick={() => setRecordOpen(true)}
            />
          ) : null}
        </div>
      </div>

      {/* Receiver actions (030/032/033/031) gated by the server's transition guards */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("recibidas.actions.title")}
          </CardTitle>
          <CardDescription>{t("recibidas.actions.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {doc.actions.map((action) => {
            const Icon = ACTION_ICON[action.eventCode];
            const busy = pendingEventCode === action.eventCode;
            const blockersText = action.blockers
              .map((blocker) => blocker.message)
              .join(" · ");
            return (
              <Button
                key={action.eventCode}
                type="button"
                variant={action.eventCode === "031" ? "destructive" : "outline"}
                disabled={!action.allowed || pendingEventCode !== null}
                title={!action.allowed ? blockersText : undefined}
                onClick={() => {
                  if (action.eventCode === "031") {
                    setClaimOpen(true);
                  } else {
                    void onEmitEvent(action.eventCode);
                  }
                }}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
                {t(`recibidas.action.${action.eventCode}`)}
              </Button>
            );
          })}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-6">
          {/* Linked purchase invoice */}
          {doc.purchaseInvoiceId ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("recibidas.linked.title")}
                </CardTitle>
                <CardDescription>
                  {t("recibidas.linked.subtitle")}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <Button asChild variant="outline" size="sm">
                  <Link
                    to="/companies/$companyId/compras/$invoiceId"
                    params={{ companyId, invoiceId: doc.purchaseInvoiceId }}
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {t("recibidas.linked.open")}
                  </Link>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void onUnlink()}
                  disabled={linkMutation.isPending}
                >
                  {linkMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Link2Off className="h-3.5 w-3.5" />
                  )}
                  {t("recibidas.linked.unlink")}
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {/* RADIAN event timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("recibidas.events.title")}
              </CardTitle>
              <CardDescription>
                {t("recibidas.events.subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadianEventTimeline
                events={doc.events}
                onDownloadArtifact={(event, name) =>
                  void onDownloadEventArtifact(event, name)
                }
                downloadingKey={downloadingKey}
              />
            </CardContent>
          </Card>

          {/* Document evidence artifacts */}
          <ReceivedDocumentArtifactsCard
            companyId={companyId}
            receivedId={receivedId}
            enabled={doc.hasArtifacts}
          />
        </div>

        {/* Summary sidebar */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-base">
              {t("recibidas.detail.summary")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row
              label={t("recibidas.detail.tax_exclusive")}
              value={formatCop(doc.taxExclusiveAmount)}
            />
            <Row
              label={t("recibidas.detail.total")}
              value={formatCop(doc.totalAmount)}
              bold
            />
            <Row
              label={t("recibidas.detail.payable")}
              value={formatCop(doc.payableAmount)}
            />
            <div className="space-y-1 border-t pt-2 text-xs text-muted-foreground">
              <p>
                {t("recibidas.detail.issue_date")}: {doc.issueDate}
              </p>
              {doc.dueDate ? (
                <p>
                  {t("recibidas.detail.due_date")}: {doc.dueDate}
                </p>
              ) : null}
              {doc.claimWindowEndsAt ? (
                <p>
                  {t("recibidas.detail.claim_window_ends")}:{" "}
                  {doc.claimWindowEndsAt}
                </p>
              ) : null}
              {doc.dianStatusDescription ? (
                <p>DIAN: {doc.dianStatusDescription}</p>
              ) : null}
              <div className="flex items-start gap-1 pt-1">
                <span className="break-all font-mono">CUFE {doc.cufe}</span>
                <button
                  type="button"
                  onClick={() => void onCopyCufe()}
                  className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label={t("recibidas.detail.copy_cufe")}
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reclamo (031) dialog */}
      <ClaimDialog
        open={claimOpen}
        onOpenChange={setClaimOpen}
        pending={pendingEventCode === "031"}
        onConfirm={async (concept, description) => {
          const ok = await onEmitEvent("031", {
            concept,
            ...(description ? { description } : {}),
          });
          if (ok) setClaimOpen(false);
        }}
      />

      {/* Registrar y contabilizar dialog */}
      <RecordPurchaseDialog
        companyId={companyId}
        receivedId={receivedId}
        open={recordOpen}
        onOpenChange={setRecordOpen}
        onRecorded={(purchaseInvoiceId) =>
          void navigate({
            to: "/companies/$companyId/compras/$invoiceId",
            params: { companyId, invoiceId: purchaseInvoiceId },
          })
        }
      />
    </div>
  );
}

// ── Reclamo (031) dialog ─────────────────────────────────────────────────────

function ClaimDialog({
  open,
  onOpenChange,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  onConfirm: (
    concept: RadianClaimConcept,
    description: string,
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [concept, setConcept] = useState<RadianClaimConcept>("01");
  const [description, setDescription] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setConcept("01");
          setDescription("");
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("recibidas.claim.title")}</DialogTitle>
          <DialogDescription>{t("recibidas.claim.body")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label htmlFor="claim-concept">
              {t("recibidas.claim.concept_label")}
            </Label>
            <Select
              value={concept}
              onValueChange={(v) => setConcept(v as RadianClaimConcept)}
            >
              <SelectTrigger id="claim-concept">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RADIAN_CLAIM_CONCEPTS.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code} — {t(`recibidas.claim.concept.${code}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="claim-description">
              {t("recibidas.claim.description_label")}
            </Label>
            <textarea
              id="claim-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={t("recibidas.claim.description_placeholder")}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onConfirm(concept, description.trim())}
            disabled={pending}
          >
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldAlert className="h-4 w-4" />
            )}
            {t("recibidas.claim.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Registrar y contabilizar dialog ──────────────────────────────────────────

function RecordPurchaseDialog({
  companyId,
  receivedId,
  open,
  onOpenChange,
  onRecorded,
}: {
  companyId: string;
  receivedId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRecorded: (purchaseInvoiceId: string) => void;
}) {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const prefillQuery = useRecordPurchasePrefill(companyId, receivedId, open);
  const recordMutation = useRecordPurchaseFromReceived(companyId);

  const [accounts, setAccounts] = useState<Record<number, string>>({});
  const [retefuente, setRetefuente] = useState("");
  const [reteiva, setReteiva] = useState("");
  const [reteica, setReteica] = useState("");

  const prefill = prefillQuery.data;

  // Seed the mapping inputs when the prefill arrives (or the dialog reopens).
  useEffect(() => {
    if (!prefill || !open) return;
    const seeded: Record<number, string> = {};
    for (const line of prefill.lines) {
      seeded[line.lineId] =
        line.suggestedAccountCode ?? prefill.supplierHabitualAccountCode ?? "";
    }
    setAccounts(seeded);
    setRetefuente(String(prefill.suggestedRetentions.retefuenteAmount ?? 0));
    setReteiva(String(prefill.suggestedRetentions.reteivaAmount ?? 0));
    setReteica(String(prefill.suggestedRetentions.reteicaAmount ?? 0));
  }, [prefill, open]);

  const allMapped =
    !!prefill &&
    prefill.lines.every((line) =>
      /^\d{4,10}$/.test(accounts[line.lineId] ?? ""),
    );

  async function onConfirm() {
    if (!prefill) return;
    try {
      const result = await recordMutation.mutateAsync({
        receivedId,
        input: {
          lines: prefill.lines.map((line) => ({
            lineId: line.lineId,
            accountCode: (accounts[line.lineId] ?? "").trim(),
          })),
          retentions: {
            retefuenteAmount: Number(retefuente) || 0,
            reteivaAmount: Number(reteiva) || 0,
            reteicaAmount: Number(reteica) || 0,
          },
        },
      });
      if (result.status === "posted" && result.entryNumber !== null) {
        toast.success(
          t("recibidas.record.toast_posted", { entry: result.entryNumber }),
          {
            action: {
              label: t("recibidas.record.open_invoice"),
              onClick: () => onRecorded(result.purchaseInvoiceId),
            },
          },
        );
      } else {
        toast.success(t("recibidas.record.toast_linked"), {
          action: {
            label: t("recibidas.record.open_invoice"),
            onClick: () => onRecorded(result.purchaseInvoiceId),
          },
        });
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(radianErrorMessage(err, t));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("recibidas.record.title")}</DialogTitle>
          <DialogDescription>{t("recibidas.record.body")}</DialogDescription>
        </DialogHeader>

        {prefillQuery.isPending ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : prefillQuery.isError || !prefill ? (
          <p className="text-sm text-destructive">
            {radianErrorMessage(prefillQuery.error, t)}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {!prefill.canRecord && prefill.blocker ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
                {prefill.blocker.message}
              </p>
            ) : null}
            {prefill.existingPurchaseInvoice ? (
              <p className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-200">
                {t("recibidas.record.existing_note", {
                  status: prefill.existingPurchaseInvoice.status,
                })}
              </p>
            ) : null}

            <div className="text-sm text-muted-foreground">
              {prefill.supplier.name} · {prefill.documentId} ·{" "}
              {prefill.issueDate}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("recibidas.record.line_description")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("recibidas.record.line_amount")}
                  </TableHead>
                  <TableHead className="text-right">IVA</TableHead>
                  <TableHead className="w-40">
                    {t("recibidas.record.line_account")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prefill.lines.map((line) => (
                  <TableRow key={line.lineId}>
                    <TableCell>
                      <p className="text-sm">{line.description}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {line.quantity} × {formatCop(line.unitPrice)}
                        {line.sku ? ` · SKU ${line.sku}` : ""}
                      </p>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCop(line.lineExtensionAmount)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {line.ivaAmount > 0
                        ? `${formatCop(line.ivaAmount)} (${line.ivaPercent}%)`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Input
                        value={accounts[line.lineId] ?? ""}
                        onChange={(e) =>
                          setAccounts((prev) => ({
                            ...prev,
                            [line.lineId]: e.target.value.trim(),
                          }))
                        }
                        placeholder="5195"
                        className="h-8 font-mono text-sm"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-[11px] text-muted-foreground">
              {t("recibidas.record.account_hint")}
            </p>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="record-retefuente">ReteFuente</Label>
                <Input
                  id="record-retefuente"
                  type="number"
                  min="0"
                  step="0.01"
                  value={retefuente}
                  onChange={(e) => setRetefuente(e.target.value)}
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="record-reteiva">ReteIVA</Label>
                <Input
                  id="record-reteiva"
                  type="number"
                  min="0"
                  step="0.01"
                  value={reteiva}
                  onChange={(e) => setReteiva(e.target.value)}
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="record-reteica">ReteICA</Label>
                <Input
                  id="record-reteica"
                  type="number"
                  min="0"
                  step="0.01"
                  value={reteica}
                  onChange={(e) => setReteica(e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={recordMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => void onConfirm()}
            disabled={
              recordMutation.isPending ||
              !prefill ||
              !prefill.canRecord ||
              !allMapped
            }
            title={
              prefill && !allMapped
                ? t("recibidas.record.mapping_incomplete")
                : undefined
            }
          >
            {recordMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <BookCheck className="h-4 w-4" />
            )}
            {t("recibidas.record.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Document artifacts card ──────────────────────────────────────────────────

function ReceivedDocumentArtifactsCard({
  companyId,
  receivedId,
  enabled,
}: {
  companyId: string;
  receivedId: string;
  enabled: boolean;
}) {
  const { t } = useTranslation();
  const artifactsQuery = useReceivedDocumentArtifacts(
    companyId,
    receivedId,
    enabled,
  );
  const [downloading, setDownloading] = useState<string | null>(null);

  async function onDownload(name: string) {
    setDownloading(name);
    try {
      const blob = await getReceivedDocumentArtifactBlob(
        companyId,
        receivedId,
        name as ReceivedDocumentArtifactName,
      );
      saveBlob(blob, `${name}.${DOC_ARTIFACT_EXT[name] ?? "bin"}`);
    } catch (err) {
      toast.error(radianErrorMessage(err, t));
    } finally {
      setDownloading(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("recibidas.artifacts.title")}
        </CardTitle>
        <CardDescription>{t("recibidas.artifacts.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        {!enabled ? (
          <p className="text-sm text-muted-foreground">
            {t("recibidas.artifacts.empty")}
          </p>
        ) : artifactsQuery.isPending ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : artifactsQuery.isError ? (
          <p className="text-sm text-destructive">
            {t("recibidas.artifacts.load_error")}
          </p>
        ) : (artifactsQuery.data?.artifacts.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("recibidas.artifacts.empty")}
          </p>
        ) : (
          <ul className="divide-y">
            {artifactsQuery.data!.artifacts.map((artifact) => (
              <li
                key={artifact.name}
                className="flex items-center justify-between gap-2 py-2"
              >
                <div className="flex items-center gap-2 truncate">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div className="truncate">
                    <p className="text-sm font-medium">
                      {t(`recibidas.artifacts.name.${artifact.name}`, {
                        defaultValue: artifact.name,
                      })}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {artifact.name}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={downloading === artifact.name}
                  onClick={() => void onDownload(artifact.name)}
                >
                  {downloading === artifact.name ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  {t("recibidas.artifacts.download")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
